import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/Navbar/Navbar';
import Footer from '../../components/Footer/Footer';
import { newsroomArticles, getStoredNewsroomState, saveNewsroomState } from './newsroomData';
import {
  MoreVertical, Eye, Heart, MessageSquare, Share2, X, Link as LinkIcon
} from 'lucide-react';
import './Newsroom.css';

// SVG Icon Helpers for Brand Logos
const FacebookIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

const TwitterIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

const LinkedinIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.28 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.75M6.46 10.9v8.37H9.25V10.9H6.46M7.86 6.74a1.62 1.62 0 1 0 0 3.24 1.62 1.62 0 0 0 0-3.24z"/>
  </svg>
);

export default function Newsroom() {
  const navigate = useNavigate();
  const [appState, setAppState] = useState(getStoredNewsroomState);
  const [activeMenuId, setActiveMenuId] = useState(null);
  const [shareArticle, setShareArticle] = useState(null);
  const [copiedToast, setCopiedToast] = useState(false);

  const [columnCount, setColumnCount] = useState(() => {
    if (typeof window === 'undefined') return 4;
    const w = window.innerWidth;
    if (w < 700) return 1;
    if (w < 1000) return 2;
    if (w < 1200) return 3;
    return 4;
  });

  useEffect(() => {
    const handleResize = () => {
      const w = window.innerWidth;
      if (w < 700) setColumnCount(1);
      else if (w < 1000) setColumnCount(2);
      else if (w < 1200) setColumnCount(3);
      else setColumnCount(4);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    saveNewsroomState(appState);
  }, [appState]);

  // Distribute articles into independent vertical columns (round-robin order: Card 1 -> Col 1, Card 2 -> Col 2, etc.)
  const columns = Array.from({ length: columnCount }, () => []);
  newsroomArticles.forEach((article, index) => {
    columns[index % columnCount].push(article);
  });

  // Click outside to close three-dot menu
  useEffect(() => {
    const handleGlobalClick = (e) => {
      if (!e.target.closest('.article-meta-row')) {
        setActiveMenuId(null);
      }
    };
    window.addEventListener('click', handleGlobalClick);
    return () => window.removeEventListener('click', handleGlobalClick);
  }, []);

  const handleArticleClick = (articleId) => {
    navigate(`/newsroom/${articleId}`);
  };

  const handleToggleLike = (e, articleId) => {
    e.stopPropagation();
    setAppState((prev) => {
      const isLiked = prev.userLikedMap[articleId];
      const currentLikes = prev.likesMap[articleId] || 0;
      const nextState = {
        ...prev,
        userLikedMap: {
          ...prev.userLikedMap,
          [articleId]: !isLiked,
        },
        likesMap: {
          ...prev.likesMap,
          [articleId]: isLiked ? currentLikes - 1 : currentLikes + 1,
        },
      };
      saveNewsroomState(nextState);
      return nextState;
    });
  };

  const toggleMenu = (e, articleId) => {
    e.stopPropagation();
    setActiveMenuId(activeMenuId === articleId ? null : articleId);
  };

  const openShareModal = (e, article) => {
    e.stopPropagation();
    setActiveMenuId(null);
    setShareArticle(article);
    setCopiedToast(false);
  };

  const closeShareModal = () => {
    setShareArticle(null);
    setCopiedToast(false);
  };

  const handleCopyLink = () => {
    if (!shareArticle) return;
    const fullUrl = `${window.location.origin}/newsroom/${shareArticle.id}`;
    navigator.clipboard.writeText(fullUrl).then(() => {
      setCopiedToast(true);
      setTimeout(() => setCopiedToast(false), 2500);
    }).catch(err => console.error('Could not copy link: ', err));
  };

  const handleSocialShare = (platform) => {
    if (!shareArticle) return;
    const url = encodeURIComponent(`${window.location.origin}/newsroom/${shareArticle.id}`);
    const text = encodeURIComponent(shareArticle.title);

    let shareUrl = '';
    if (platform === 'facebook') {
      shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${url}`;
    } else if (platform === 'twitter') {
      shareUrl = `https://twitter.com/intent/tweet?url=${url}&text=${text}`;
    } else if (platform === 'linkedin') {
      shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${url}`;
    }

    if (shareUrl) {
      window.open(shareUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div className="newsroom-wrapper">
      <Navbar />

      <main className="newsroom-container">
        <div className="newsroom-header-left">
          <h1 className="newsroom-all-posts-heading">All Posts</h1>
        </div>

        <div className="newsroom-columns">
          {columns.map((colArticles, colIdx) => (
            <div key={colIdx} className="newsroom-column">
              {colArticles.map((article) => {
                const viewsCount = appState.viewsMap[article.id] || 0; // Starts at 0, dynamically increments when card is clicked/opened
                const likesCount = appState.likesMap[article.id] ?? article.likes;
                const isLiked = !!appState.userLikedMap[article.id];
                const isMenuOpen = activeMenuId === article.id;

                return (
                  <article key={article.id} className="article-card">
                    {/* Thumbnail */}
                    <div
                      className="article-thumbnail-wrapper"
                      onClick={() => handleArticleClick(article.id)}
                    >
                      <img
                        src={article.imageUrl}
                        alt={article.title}
                        className="article-thumbnail"
                        loading="lazy"
                      />
                    </div>

                    {/* Content */}
                    <div className="article-content">
                      {/* Meta row */}
                      <div className="article-meta-row">
                        <span className="article-meta-left">
                          {article.date} · {article.readTime}
                        </span>

                        <button
                          className="three-dots-btn"
                          onClick={(e) => toggleMenu(e, article.id)}
                          aria-label="Article options"
                        >
                          <MoreVertical size={18} />
                        </button>

                        {isMenuOpen && (
                          <div className="three-dots-menu">
                            <button
                          className="menu-item-btn"
                          onClick={(e) => openShareModal(e, article)}
                        >
                          <Share2 size={15} />
                          <span>Share Post</span>
                        </button>
                      </div>
                        )}
                      </div>

                      {/* Title */}
                      <a
                        className="article-title-link"
                        onClick={(e) => {
                          e.preventDefault();
                          handleArticleClick(article.id);
                        }}
                      >
                        <h2 className="article-title">{article.title}</h2>
                      </a>

                      {/* Excerpt */}
                      <a
                        className="article-excerpt-link"
                        onClick={(e) => {
                          e.preventDefault();
                          handleArticleClick(article.id);
                        }}
                      >
                        <p className="article-excerpt">
                          {(() => {
                            if (article.fullContent) {
                              const match = article.fullContent.match(/<p>(.*?)<\/p>/s);
                              if (match && match[1]) {
                                return match[1].replace(/<[^>]+>/g, '').trim();
                              }
                            }
                            return article.excerpt;
                          })()}
                        </p>
                      </a>

                      <hr className="article-divider" />

                      {/* Footer Stats */}
                      <div className="article-footer-stats">
                        <div className="stats-left">
                          <span className="stat-item" title="Views">
                            <Eye size={15} />
                            <span>{viewsCount}</span>
                          </span>

                          <span className="stat-item" title="Comments">
                            <MessageSquare size={15} />
                            <span>{article.commentsCount}</span>
                          </span>
                        </div>

                        <button
                          className={`like-btn ${isLiked ? 'liked' : ''}`}
                          onClick={(e) => handleToggleLike(e, article.id)}
                          aria-label="Like article"
                        >
                          <Heart size={16} />
                          <span>{likesCount}</span>
                        </button>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          ))}
        </div>
      </main>

      {/* SHARE MODAL OVERLAY */}
      {shareArticle && (
        <div className="modal-overlay" onClick={closeShareModal}>
          <div
            className="share-modal-card"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="modal-close-btn"
              onClick={closeShareModal}
              aria-label="Close modal"
            >
              <X size={18} />
            </button>

            <h3 className="share-modal-title">Share Post</h3>

            <div className="share-icons-row">
              <button
                className="share-icon-btn share-btn-facebook"
                onClick={() => handleSocialShare('facebook')}
                title="Share on Facebook"
              >
                <FacebookIcon />
              </button>

              <button
                className="share-icon-btn share-btn-twitter"
                onClick={() => handleSocialShare('twitter')}
                title="Share on X (Twitter)"
              >
                <TwitterIcon />
              </button>

              <button
                className="share-icon-btn share-btn-linkedin"
                onClick={() => handleSocialShare('linkedin')}
                title="Share on LinkedIn"
              >
                <LinkedinIcon />
              </button>

              <button
                className="share-icon-btn share-btn-copy"
                onClick={handleCopyLink}
                title="Copy Link"
              >
                <LinkIcon size={22} />
              </button>
            </div>

            {copiedToast && (
              <div className="copy-toast">
                ✓ Link copied to clipboard!
              </div>
            )}
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
